import axios from 'axios';


const RATE_LIMIT_KEY = 'rateLimit:sensitiveOp:unlockAt';
const SHOP_BOOTSTRAP_CACHE_KEY = 'shop:bootstrap:lite:v1';
const GET_DEDUPE_TTL_MS = 1200;
const DEFAULT_API_TIMEOUT_MS = 30000;
const DEFAULT_CRITICAL_API_TIMEOUT_MS = 60000;
const DEFAULT_UPLOAD_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes for large file uploads
const inflightGetRequests = new Map();
const recentGetResponses = new Map();
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const parseTimeoutEnv = (rawValue, fallback) => {
  const parsed = Number.parseInt(String(rawValue ?? ''), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const API_TIMEOUT_MS = parseTimeoutEnv(process.env.REACT_APP_API_TIMEOUT_MS, DEFAULT_API_TIMEOUT_MS);
const CRITICAL_API_TIMEOUT_MS = Math.max(
  API_TIMEOUT_MS,
  parseTimeoutEnv(process.env.REACT_APP_API_CRITICAL_TIMEOUT_MS, DEFAULT_CRITICAL_API_TIMEOUT_MS)
);
const UPLOAD_TIMEOUT_MS = parseTimeoutEnv(process.env.REACT_APP_UPLOAD_TIMEOUT_MS, DEFAULT_UPLOAD_TIMEOUT_MS);

const stableSerialize = (value) => {
  if (value == null) return '';
  if (Array.isArray(value)) return `[${value.map((item) => stableSerialize(item)).join(',')}]`;
  if (typeof value === 'object') {
    return `{${Object.keys(value).sort().map((key) => `${key}:${stableSerialize(value[key])}`).join(',')}}`;
  }
  return String(value);
};

const buildGetRequestKey = (config = {}) => {
  const url = String(config.url || '');
  const method = String(config.method || 'get').toLowerCase();
  const params = stableSerialize(config.params || {});
  return `${method}:${url}?${params}`;
};

const clearExpiredGetCacheEntries = () => {
  const now = Date.now();
  for (const [key, entry] of recentGetResponses.entries()) {
    if (!entry || (now - Number(entry.cachedAt || 0)) > GET_DEDUPE_TTL_MS) {
      recentGetResponses.delete(key);
    }
  }
};

const shouldUseCriticalTimeout = (url) => {
  const path = String(url || '').toLowerCase();
  if (!path) return false;
  return path.includes('/shop/bootstrap')
    || path.includes('/auth/login')
    || path.includes('/auth/verify');
};

const shouldUseUploadTimeout = (url) => {
  const path = String(url || '').toLowerCase();
  // Match any route ending with /upload or containing /upload/ (excludes /upload-template)
  return /\/upload(\/|$)/.test(path);
};

const resolveRequestTimeout = (config = {}) => {
  const configuredTimeout = Number(config.timeout);
  if (Number.isFinite(configuredTimeout) && configuredTimeout > 0) {
    return configuredTimeout;
  }
  if (shouldUseUploadTimeout(config.url)) {
    return UPLOAD_TIMEOUT_MS;
  }
  if (shouldUseCriticalTimeout(config.url)) {
    return CRITICAL_API_TIMEOUT_MS;
  }
  return API_TIMEOUT_MS;
};

const emitGlobalToast = (message, severity = 'info') => {
  if (typeof window === 'undefined') return;
  const text = String(message || '').trim();
  if (!text) return;
  window.dispatchEvent(new CustomEvent('app:notify', {
    detail: { message: text, severity }
  }));
};

const isPermissionDenied = (error) => Number(error?.response?.status) === 403;

export const isAccessDeniedError = (error) => !!(error && (error.isAccessDenied || Number(error?.response?.status) === 403));

const TOAST_VERB_BY_METHOD = {
  post: { success: 'created', error: 'create' },
  put: { success: 'updated', error: 'update' },
  patch: { success: 'updated', error: 'update' },
  delete: { success: 'deleted', error: 'delete' }
};

const TOAST_ACTION_BY_SEGMENT = {
  approve: { success: 'approved', error: 'approve' },
  archive: { success: 'archived', error: 'archive' },
  cancel: { success: 'cancelled', error: 'cancel' },
  close: { success: 'closed', error: 'close' },
  complete: { success: 'completed', error: 'complete' },
  disable: { success: 'disabled', error: 'disable' },
  enable: { success: 'enabled', error: 'enable' },
  export: { success: 'exported', error: 'export' },
  import: { success: 'imported', error: 'import' },
  reconcile: { success: 'reconciled', error: 'reconcile' },
  reject: { success: 'rejected', error: 'reject' },
  reopen: { success: 'reopened', error: 'reopen' },
  restore: { success: 'restored', error: 'restore' },
  run: { success: 'ran', error: 'run' },
  send: { success: 'sent', error: 'send' },
  start: { success: 'started', error: 'start' },
  stop: { success: 'stopped', error: 'stop' },
  submit: { success: 'submitted', error: 'submit' },
  sync: { success: 'synced', error: 'sync' },
  test: { success: 'tested', error: 'test' },
  unarchive: { success: 'unarchived', error: 'unarchive' },
  unassign: { success: 'unassigned', error: 'unassign' },
  verify: { success: 'verified', error: 'verify' },
  void: { success: 'voided', error: 'void' }
};

const TOAST_RESOURCE_SEGMENT_IGNORED = new Set(['api', 'shop', 'auth', 'v1', 'v2', 'v3']);

const TOAST_RESOURCE_LABEL_OVERRIDES = {
  ap: 'AP',
  ar: 'AR',
  bom: 'BOM',
  cogs: 'COGS',
  gl: 'GL',
  grn: 'GRN',
  grns: 'GRNs',
  hr: 'HR',
  pos: 'POS'
};

const toNonEmptyString = (...values) => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
};

const parseRequestBody = (config) => {
  const raw = config?.data;
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  if (typeof raw !== 'string') return null;
  try {
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
};

const getPathSegments = (url) => {
  const raw = String(url || '').trim();
  if (!raw) return [];

  let path = raw;
  if (/^https?:\/\//i.test(raw)) {
    try {
      path = new URL(raw).pathname || '/';
    } catch (_) {
      path = raw;
    }
  }

  return path
    .split('?')[0]
    .split('/')
    .map((segment) => segment.trim())
    .filter(Boolean);
};

const isAuthLoginRequest = (config) => {
  const segments = getPathSegments(config?.url).map((segment) => segment.toLowerCase());
  return segments.includes('auth') && segments.includes('login');
};

const isLikelyIdentifierSegment = (segment) => {
  const value = String(segment || '').trim();
  if (!value) return true;
  if (/^\d+$/.test(value)) return true;
  if (/^tmp-\d+$/i.test(value)) return true;
  if (/^[0-9a-f]{8,}$/i.test(value)) return true;
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)) return true;
  return false;
};

const singularizeWord = (value) => {
  if (value.endsWith('ies') && value.length > 3) return `${value.slice(0, -3)}y`;
  if (value.endsWith('sses') && value.length > 4) return value.slice(0, -2);
  if (value.endsWith('ses') && value.length > 3) return value.slice(0, -2);
  if (value.endsWith('s') && !value.endsWith('ss') && value.length > 3) return value.slice(0, -1);
  return value;
};

const humanizeSegment = (segment, { singular = false } = {}) => {
  const words = String(segment || '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!words.length) return '';

  return words.map((word) => {
    const lower = String(word || '').toLowerCase();
    const normalized = singular ? singularizeWord(lower) : lower;
    if (TOAST_RESOURCE_LABEL_OVERRIDES[normalized]) {
      return TOAST_RESOURCE_LABEL_OVERRIDES[normalized];
    }
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  }).join(' ');
};

const toSentenceCase = (value) => {
  const text = String(value || '').trim();
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const extractServerMessage = (payload) => {
  if (!payload || typeof payload !== 'object') return '';

  const direct = toNonEmptyString(payload.message, payload.error, payload.detail, payload.title);
  if (direct) return direct;

  if (Array.isArray(payload.errors)) {
    for (const entry of payload.errors) {
      const message = (typeof entry === 'string')
        ? toNonEmptyString(entry)
        : toNonEmptyString(entry?.message, entry?.msg, entry?.detail);
      if (message) return message;
    }
  }

  if (payload.errors && typeof payload.errors === 'object') {
    for (const value of Object.values(payload.errors)) {
      if (Array.isArray(value)) {
        const message = toNonEmptyString(...value);
        if (message) return message;
        continue;
      }

      const message = (typeof value === 'string')
        ? toNonEmptyString(value)
        : toNonEmptyString(value?.message, value?.msg, value?.detail);
      if (message) return message;
    }
  }

  return '';
};

const resolveToastDescriptor = (config = {}) => {
  const method = String(config?.method || '').toLowerCase();
  const segments = getPathSegments(config?.url);
  const lowerSegments = segments.map((segment) => segment.toLowerCase());
  const payload = parseRequestBody(config);

  if (isAuthLoginRequest(config)) {
    return {
      successMessage: 'Signed in successfully',
      errorMessage: 'Sign in failed',
      subject: 'sign in',
      successVerb: 'completed',
      errorVerb: 'complete'
    };
  }

  if (lowerSegments.includes('shop') && lowerSegments.includes('settings')) {
    const section = toNonEmptyString(payload?.section);
    const subject = section ? `${humanizeSegment(section)} settings` : 'Settings';
    return {
      subject,
      successVerb: 'saved',
      errorVerb: 'save'
    };
  }

  const actionIndex = lowerSegments.reduce((latestIndex, segment, index) => (
    TOAST_ACTION_BY_SEGMENT[segment] ? index : latestIndex
  ), -1);

  const meaningfulSegments = lowerSegments
    .map((segment, index) => ({ segment, index }))
    .filter(({ segment }) => (
      !TOAST_RESOURCE_SEGMENT_IGNORED.has(segment)
      && !TOAST_ACTION_BY_SEGMENT[segment]
      && !isLikelyIdentifierSegment(segment)
    ));

  let resourceSegment = '';
  if (actionIndex >= 0) {
    const beforeAction = meaningfulSegments.filter(({ index }) => index < actionIndex);
    if (beforeAction.length > 0) {
      const nearest = beforeAction[beforeAction.length - 1];
      resourceSegment = segments[nearest.index] || '';
    }
  } else if (meaningfulSegments.length > 0) {
    const nearest = meaningfulSegments[meaningfulSegments.length - 1];
    resourceSegment = segments[nearest.index] || '';
  }

  const verbs = (actionIndex >= 0)
    ? TOAST_ACTION_BY_SEGMENT[lowerSegments[actionIndex]]
    : TOAST_VERB_BY_METHOD[method] || { success: 'completed', error: 'complete' };

  const shouldSingularize = method === 'post' && actionIndex < 0;
  const subject = humanizeSegment(resourceSegment, { singular: shouldSingularize }) || 'Record';

  return {
    subject,
    successVerb: verbs.success,
    errorVerb: verbs.error
  };
};

const buildContextualSuccessMessage = (config) => {
  const descriptor = resolveToastDescriptor(config);
  if (descriptor.successMessage) return descriptor.successMessage;
  return `${toSentenceCase(descriptor.subject || 'Record')} ${descriptor.successVerb || 'completed'} successfully`;
};

const buildContextualErrorMessage = (config) => {
  const descriptor = resolveToastDescriptor(config);
  if (descriptor.errorMessage) return descriptor.errorMessage;
  const subject = String(descriptor.subject || 'operation').toLowerCase();
  return `Failed to ${descriptor.errorVerb || 'complete'} ${subject}`;
};

const shouldToastForMethod = (config) => {
  const method = String(config?.method || '').toLowerCase();
  if (method !== 'post' && method !== 'put') return false;
  if (config?.meta?.suppressToast === true) return false;
  if (config?.headers?.['x-suppress-toast'] === '1') return false;
  return true;
};

const isReadOnlyMethod = (config) => {
  const method = String(config?.method || 'get').toLowerCase();
  return method === 'get' || method === 'head' || method === 'options';
};

const buildSuccessToastMessage = (response) => {
  const data = response?.data;
  const serverMessage = toNonEmptyString(data?.message, data?.data?.message);
  if (serverMessage) return serverMessage;
  return buildContextualSuccessMessage(response?.config);
};

const buildErrorToastMessage = (error) => {
  if (isPermissionDenied(error)) return 'Access denied';
  const data = error?.response?.data;
  const serverMessage = extractServerMessage(data);
  if (serverMessage) return serverMessage;
  if (error?.code === 'ECONNABORTED') return 'Request timed out. Please try again.';
  if (!error?.response) return 'Network error. Please check your connection and try again.';
  return buildContextualErrorMessage(error?.config);
};

const getInitializationRedirectUrl = (response) => {
  const redirectPath = response?.data?.redirect || '/initialization';
  const hasExplicitApiBase = !!process.env.REACT_APP_API_URL;
  const devFrontendPort = String(process.env.REACT_APP_DEV_FRONTEND_PORT || '').trim();
  const devBackendPort = String(process.env.REACT_APP_DEV_BACKEND_PORT || '').trim();

  if (!hasExplicitApiBase && typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
    if (
      devFrontendPort
      && devBackendPort
      && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
      && String(window.location.port || '') === devFrontendPort
    ) {
      return `http://localhost:${devBackendPort}${redirectPath.startsWith('/') ? redirectPath : `/${redirectPath}`}`;
    }
  }

  const fallbackApiBase = typeof window !== 'undefined'
    ? `${window.location.origin}/api`
    : '/api';
  const apiBase = process.env.REACT_APP_API_URL || fallbackApiBase;

  if (/^https?:\/\//i.test(redirectPath)) {
    return redirectPath;
  }

  try {
    const apiUrl = new URL(apiBase);
    const isLocalHost = apiUrl.hostname === 'localhost' || apiUrl.hostname === '127.0.0.1';
    const protocol = isLocalHost ? 'http:' : apiUrl.protocol;
    const backendOrigin = `${protocol}//${apiUrl.host}`;
    return `${backendOrigin}${redirectPath.startsWith('/') ? redirectPath : `/${redirectPath}`}`;
  } catch (_) {
    return redirectPath;
  }
};

// Create axios instance with base configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || (typeof window !== 'undefined' ? `${window.location.origin}/api` : '/api'),
  timeout: API_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
  },
  validateStatus: (status) => {
    if (status === 304) return true; // treat cached responses as success instead of throwing
    return status >= 200 && status < 300;
  }
});

const baseRequest = api.request.bind(api);
api.request = (configOrUrl, maybeConfig) => {
  const config = typeof configOrUrl === 'string'
    ? { ...(maybeConfig || {}), url: configOrUrl }
    : { ...(configOrUrl || {}) };
  config.timeout = resolveRequestTimeout(config);
  const method = String(config.method || 'get').toLowerCase();
  const shouldDedupe = (method === 'get' || method === 'head')
    && config?.meta?.disableGetDedupe !== true;

  if (!shouldDedupe) {
    return baseRequest(config);
  }

  clearExpiredGetCacheEntries();
  const cacheKey = buildGetRequestKey(config);
  const cached = recentGetResponses.get(cacheKey);
  if (cached && (Date.now() - cached.cachedAt) <= GET_DEDUPE_TTL_MS) {
    return Promise.resolve(cached.response);
  }

  if (inflightGetRequests.has(cacheKey)) {
    return inflightGetRequests.get(cacheKey);
  }

  const requestPromise = baseRequest(config)
    .then((response) => {
      recentGetResponses.set(cacheKey, {
        cachedAt: Date.now(),
        response
      });
      return response;
    })
    .finally(() => {
      inflightGetRequests.delete(cacheKey);
    });

  inflightGetRequests.set(cacheKey, requestPromise);
  return requestPromise;
};

if (typeof window !== 'undefined') {
  // Fully-online mode: clear any stale offline queue entries from prior sessions.
  try {
    window.localStorage.removeItem('offline:outbox:v1');
  } catch (_) {
    // ignore storage access issues
  }

  // Suppress unhandled promise rejections for access-denied errors so they
  // do not bubble up to React error boundaries and cause full red screens.
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason;
    if (error && (error.isAccessDenied || isAccessDeniedError(error))) {
      event.preventDefault();
    }
  });
}

// Request interceptor to add auth token
api.interceptors.request.use(
  async (config) => {
    if (!config.headers) {
      config.headers = {};
    }

    const method = (config.method || 'get').toLowerCase();
    if (method !== 'get' && method !== 'head') {
      try {
        const stored = localStorage.getItem(RATE_LIMIT_KEY);
        if (stored) {
          const unlockAt = parseInt(stored, 10);
          if (!Number.isNaN(unlockAt)) {
            const now = Date.now();
            if (unlockAt > now) {
              await delay(Math.max(unlockAt - now, 0));
            } else {
              localStorage.removeItem(RATE_LIMIT_KEY);
            }
          }
        }
      } catch (_) {
        // ignore storage access issues
      }
    }

    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    if (shouldToastForMethod(response?.config)) {
      emitGlobalToast(buildSuccessToastMessage(response), 'success');
    }
    return response;
  },
  async (error) => {
    const accessDenied = isPermissionDenied(error);
    const skipAuthRedirect = error?.config?.meta?.skipAuthRedirect === true || isAuthLoginRequest(error?.config);

    if (isPermissionDenied(error)) {
      // Keep backend ACL strict, but avoid noisy toasts for read-only/background UI fetches.
      if (!isReadOnlyMethod(error?.config)) {
        emitGlobalToast('Access denied', 'error');
      }
      error.message = 'Access denied';
      if (error.response && error.response.data) {
        error.response.data.message = 'Access denied';
        error.response.data.ui_suppressed = true;
      }
      error.isAccessDenied = true;
      error.suppressUiError = true;
    }

    if (!accessDenied && shouldToastForMethod(error?.config)) {
      emitGlobalToast(buildErrorToastMessage(error), 'error');
    }

    const status = error.response?.status;
    if (status === 401) {
      if (skipAuthRedirect) {
        return Promise.reject(error);
      }
      // Token expired or invalid
      localStorage.removeItem('token');
      try {
        window.sessionStorage.removeItem(SHOP_BOOTSTRAP_CACHE_KEY);
      } catch (_) {
        // ignore storage errors
      }
      const sessionReplaced = String(error?.response?.data?.code || '').toUpperCase() === 'SESSION_REPLACED';
      if (sessionReplaced) {
        emitGlobalToast('Your account was signed in on another device. Please login again.', 'warning');
      }
      if (typeof window !== 'undefined' && String(window.location?.pathname || '').toLowerCase() !== '/login') {
        window.location.href = '/login';
      }
    }
    else if (status === 503 && error.response?.data?.code === 'NOT_INITIALIZED') {
      window.location.href = getInitializationRedirectUrl(error.response);
      return Promise.reject(error);
    }
    else if (status === 429) {
      const headers = error.response?.headers || {};
      const retryAfterHeader = headers['retry-after'] || headers['Retry-After'];
      let retryAfterSeconds = 0;
      if (retryAfterHeader) {
        const parsed = parseInt(retryAfterHeader, 10);
        if (!Number.isNaN(parsed)) retryAfterSeconds = parsed;
      } else if (typeof error.response?.data?.retry_after === 'number') {
        retryAfterSeconds = error.response.data.retry_after;
      }
      if (!Number.isFinite(retryAfterSeconds) || retryAfterSeconds <= 0) {
        retryAfterSeconds = 15; // sane fallback
      }
      const unlockAt = Date.now() + (retryAfterSeconds * 1000);
      try {
        localStorage.setItem(RATE_LIMIT_KEY, String(unlockAt));
        window.dispatchEvent(new CustomEvent('rate-limit', {
          detail: {
            key: 'sensitiveOp',
            unlockAt,
            retryAfterSeconds
          }
        }));
      } catch (_) {
        // ignore storage errors
      }

      const originalConfig = error.config;
      if (originalConfig && !originalConfig.__isRetry) {
        originalConfig.__isRetry = true;
        await delay(retryAfterSeconds * 1000);
        return api(originalConfig);
      }
    }
    return Promise.reject(error);
  }
);


// Helper to fetch routers (used in Network and Customers)
api.getRouters = function() {
  return api.get('/routers');
};

export default api;
